/*
  Customer reviews, shared by the reviews page (snippets/reviews-browser.liquid)
  and the product page (snippets/product-reviews.liquid): a class that queries
  the reviews index, the card markup and the masonry that lays cards out.
*/

class ReviewSearch extends EventTarget {
  algolia = algoliasearch.algoliasearch("M00M3RFW1A", "8bb568294f64d0f5398d3fbc41b78c9d")
  requestCount = 0
  query = ""
  productId = null
  featuredOnly = false
  page = 1
  pageSize = 30

  search() {
    let requestId = ++this.requestCount
    // typos are allowed only when nothing matches exactly
    let params = {page: this.page - 1, hitsPerPage: this.pageSize, typoTolerance: "min"}
    let query = this.query
    // the index has no product facet, so a product's reviews are found by searching its id
    if(this.productId) {
      query = String(this.productId)
      params.restrictSearchableAttributes = ["product.id"]
      params.typoTolerance = false
    }
    // reviews picked out as worth showing off carry the feature flag
    if(this.featuredOnly) params.facetFilters = [["feature:true"]]
    this.algolia.search([{indexName: "reviews", query: query, params: params}]).then(({results}) => {
      if(requestId != this.requestCount) return
      this.dispatchEvent(new CustomEvent("results", {detail: {
        hits: results[0].hits, hitCount: results[0].nbHits, page: results[0].page, pageCount: results[0].nbPages,
      }}))
    })
  }
}

// the index stores comments with entities already encoded, so decode before escaping
const decodeReviewText = (text) => { let t = document.createElement("textarea"); t.innerHTML = text || ""; return t.value }
const escapeReviewText = (text) => $("<div>").text(decodeReviewText(text)).html().replace(/\r?\n/g, "<br>")

// "3 days ago", "2 years ago"
const reviewAgo = (unix) => {
  let relative = new Intl.RelativeTimeFormat("en", {numeric: "auto"})
  let seconds = Math.round(unix - Date.now() / 1000)
  let steps = [["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["minute", 60]]
  for(let [unit, size] of steps) if(Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit)
  return "just now"
}

/*
  a review card. options.product false leaves out the product the review is
  about, for pages that are already about it
*/
const createReviewHtml = (hit, options = {}) => {
  let escape = escapeReviewText
  let showProduct = options.product ?? true
  let product = hit.product && hit.source != "gcr" ? hit.product : null
  let name = hit.reviewer?.first_name && hit.reviewer.first_name != "Anonymous" ? hit.reviewer.first_name : "A customer"
  // the town alone: addresses arrive as "Bergen, NO" or "Gloucester, United Kingdom"
  let town = (hit.reviewer?.address || "").split(",")[0].replace(/["']/g, "").trim()
  let place = town ? ` <span>from ${escape(town)}</span>` : ""
  let avatar = hit.reviewer?.gravatar ? `<img class="avatar" src="https://www.gravatar.com/avatar/${hit.reviewer.gravatar}?d=mp&s=80" width="40" height="40" alt="" loading="lazy">` : ""
  let about = ""
  let thumb = ""
  if(showProduct) {
    about = product
      ? `<br>about <a href="${shopRoot}/products/${product.handle}">${escape(product.title)}</a>`
      : `<br>about shopping with us, via <a href="https://www.google.com/shopping/customerreviews/merchantreviews?q=pimoroni.com">Google Customer Reviews</a>`
    thumb = product?.image ? `<a class="product" href="${shopRoot}/products/${product.handle}"><img src="${product.image.replace(/(\.[a-z]+)(\?|$)/, "_150x150$1$2")}" width="56" height="56" alt="" loading="lazy"></a>` : ""
  }
  let photos = (hit.images || []).map(image => {
    let url = image.image.replace("https://s3-eu-west-1.amazonaws.com/reviewscouk/assets/", "https://cdn.review-images.pimoroni.com/")
    return `<a href="${url}?width=1600&optimizer=image" target="_blank" rel="noopener"><img src="${url}?width=400&optimizer=image" alt="Customer photo" loading="lazy"></a>`
  }).join("")

  return `
  <article class="review"><div class="inner">
    <header>
      <star-rating rating="${hit.score}"></star-rating>
      <time datetime="${new Date(hit.date * 1000).toISOString()}">${reviewAgo(hit.date)}</time>
    </header>
    <p class="comment">${escape(hit.comment)}</p>
    ${photos ? `<div class="photos">${photos}</div>` : ""}
    <footer>
      ${avatar}
      <div><b>${escape(name)}</b>${place}${about}</div>
      ${thumb}
    </footer>
  </div></article>`
}

/*
  masonry for a .review-grid: the grid runs on 1px rows and each card spans as
  many as its content needs, so cards pack upwards under shorter neighbours while
  keeping their left to right date order. A ResizeObserver keeps the spans right as
  photos load and the window changes. Browsers with CSS masonry lay the grid out
  themselves and skip all this.
*/
class ReviewMasonry {
  constructor(grid) {
    this.grid = grid
    let native = CSS.supports("grid-template-rows", "masonry") || CSS.supports("display", "masonry")
    this.sizer = native ? null : new ResizeObserver(entries => { for(let entry of entries) this.fit(entry.target.parentElement) })
  }

  gap() {
    return parseFloat(getComputedStyle(this.grid).columnGap) || 0
  }

  fit(card) {
    card.style.gridRowEnd = `span ${Math.ceil(card.firstElementChild.getBoundingClientRect().height + this.gap())}`
  }

  clear() {
    this.grid.replaceChildren()
  }

  add(card) {
    this.grid.append(card)
    if(this.sizer) {
      this.fit(card)
      this.sizer.observe(card.firstElementChild)
    }
  }
}
